var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { ChevronDownIcon, ChevronDownSmallIcon } from '@inter-ikea-kompis/icons';
import { IdGenerator } from '@inter-ikea-kompis/utilities';
import { Collapsible } from '@inter-ikea-kompis/component-collapsible';
import { FocusOutline } from '@inter-ikea-kompis/component-focus-outline';
import { Icon } from '@inter-ikea-kompis/component-icon';
import { Text } from '@inter-ikea-kompis/component-text';
import TextToggleLayoutEnum from '../../enums/TextToggleLayoutEnum.js';
import TextToggleSizeEnum from '../../enums/TextToggleSizeEnum.js';
import TextToggleStyle from './TextToggleStyle.js';
/**
 * Text toggle.
 *
 * @event expand Triggered when toggle is clicked
 * @event animationEnd Triggered when animation ends
 */
export default class TextToggle extends KompisElement {
    constructor() {
        super(...arguments);
        // Layout
        this.layout = TextToggleLayoutEnum.subtle;
        // Label
        this.label = null;
        // Label displayed when printing
        this.printLabel = null;
        // Label explaining the text toggle further for screen readers. If supplied, "label" won't be read by screen readers.
        this.ariaLabel = null;
        // Expanded
        this.expanded = false;
        // Size
        this.size = TextToggleSizeEnum.medium;
        this.uniqueId = new IdGenerator().generateId();
    }
    /**
     * Renders the component.
     *
     * @returns Template.
     */
    render() {
        return html `
			<style>
				${TextToggleStyle.getStyle({
            expanded: this.expanded,
            layout: this.layout,
            theme: this.theme,
            printLabel: this.printLabel
        })}
			</style>
			<button
				aria-expanded="${this.expanded}"
				aria-controls="${this.uniqueId}"
				@click="${this.onClick.bind(this)}"
			>
				<kompis-icon
					.theme="${this.theme}"
					.icon="${this.size === TextToggleSizeEnum.medium
            ? ChevronDownIcon
            : ChevronDownSmallIcon}"
				></kompis-icon>
				<div class="divider"></div>
				<kompis-text
					.theme="${this.theme}"
					.type="${this.size === TextToggleSizeEnum.medium
            ? ThemeFontStyleTypeEnum.bodyMedium
            : ThemeFontStyleTypeEnum.bodySmall}"
					.bold="${true}"
				>
					${this.getLabel()}
				</kompis-text>
			</button>
			<kompis-collapsible
				id="${this.uniqueId}"
				.expanded="${this.expanded}"
				@animationEnd="${() => this.dispatchEvent(new CustomEvent('animationEnd', { bubbles: true }))}"
			>
				<slot></slot>
			</kompis-collapsible>
		`;
    }
    connectedCallback() {
        this.customElements.define('kompis-focus-outline', FocusOutline);
        this.customElements.define('kompis-icon', Icon);
        this.customElements.define('kompis-text', Text);
        this.customElements.define('kompis-collapsible', Collapsible);
        super.connectedCallback();
    }
    getLabel() {
        if (!this.ariaLabel) {
            return html `<span>${this.label}</span>`;
        }
        else {
            return html `<span class="visuallyHidden">${this.ariaLabel}</span
				><span aria-hidden="true">${this.label}</span>`;
        }
    }
    /**
     * On click.
     */
    onClick() {
        this.dispatchEvent(new CustomEvent('expand', {
            bubbles: true,
            detail: {
                expanded: !this.expanded
            }
        }));
    }
}
__decorate([
    property({ type: String })
], TextToggle.prototype, "layout", void 0);
__decorate([
    property({ type: String })
], TextToggle.prototype, "label", void 0);
__decorate([
    property({ type: String })
], TextToggle.prototype, "printLabel", void 0);
__decorate([
    property({ type: String })
], TextToggle.prototype, "ariaLabel", void 0);
__decorate([
    property({ type: Boolean })
], TextToggle.prototype, "expanded", void 0);
__decorate([
    property({ type: String })
], TextToggle.prototype, "size", void 0);
__decorate([
    property({ type: Object, required: true })
], TextToggle.prototype, "theme", void 0);
//# sourceMappingURL=TextToggle.js.map